import { getActivePage, getSceneNodeChildren, type CurvaDocument } from "@/document/model/document";
import type { SceneNode } from "@/document/model/nodes";
import { isNodeLockedForEditing } from "@/document/operations/nodeLocking";
import { findDirectParentContainer } from "@/editor/tools/measureOverlay";

export function selectAllNodeIds(
  document: CurvaDocument,
  activePageId: string | undefined,
  selectedNodeIds: string[],
  primarySelectedNodeId?: string,
): string[] {
  const scope = selectedNodeIds.length > 0
    ? sharedSiblingScope(document, selectedNodeIds)
      ?? siblingScopeChildren(document, primarySelectedNodeId ?? selectedNodeIds.at(-1)!)
    : topLevelPageChildren(document, activePageId);

  return selectableNodeIds(document, scope);
}

function topLevelPageChildren(document: CurvaDocument, activePageId?: string): SceneNode[] {
  return getActivePage(document, activePageId)?.children ?? [];
}

function siblingScopeChildren(document: CurvaDocument, nodeId: string): SceneNode[] {
  const parentContainer = findDirectParentContainer(document, nodeId);
  if (parentContainer) {
    return getSceneNodeChildren(parentContainer) ?? [];
  }

  for (const page of document.pages) {
    if (page.children.some(child => child.id === nodeId)) {
      return page.children;
    }
  }

  return [];
}

function sharedSiblingScope(document: CurvaDocument, nodeIds: string[]): SceneNode[] | undefined {
  const selected = new Set(nodeIds);
  for (const page of document.pages) {
    const scope = findSiblingScopeInNodes(page.children, selected);
    if (scope) {
      return scope;
    }
  }

  return undefined;
}

function findSiblingScopeInNodes(nodes: SceneNode[], selected: Set<string>): SceneNode[] | undefined {
  const selectedCount = nodes.filter(node => selected.has(node.id)).length;
  if (selectedCount > 0 && selectedCount === selected.size) {
    return nodes;
  }

  for (const node of nodes) {
    const children = getSceneNodeChildren(node);
    if (!children) {
      continue;
    }

    const scope = findSiblingScopeInNodes(children, selected);
    if (scope) {
      return scope;
    }
  }

  return undefined;
}

function selectableNodeIds(document: CurvaDocument, nodes: SceneNode[]): string[] {
  return nodes
    .filter(node => node.visible && !isNodeLockedForEditing(document, node.id))
    .map(node => node.id);
}
